import React, {useContext, useState, useEffect, useReducer} from "react";
import {STEP_NUMBERS} from "./Data";
import {Divider, Form, InputNumber, Radio, Select, Space, Switch, Tooltip} from "antd";
import Button from "@common/Button";
import ButtonsGroup from "@common/ButtonsGroup";
import PopConfirm from "@common/PopConfirm";
import Card from "@common/Card";
import CircularLoader from "@common/CircularLoader";
import Checkbox from "@common/Checkbox";
import MuiTooltip from "@common/Tooltip";
import {ArrowLeftIcon, HelpIcon} from "@common/icons";
import {BrandContext} from "@context/BrandContext";
import {cellStylePercentage} from "../../../../diagnosticReporting/Utils";
import {AGGREGATE_LEVELS, COMPARISON_TYPE, GRID_ENUMS, GridStyle} from "../../../../diagnosticReporting/Styles.js";
import axios from "@common/axios";
import {AgGridReact} from "ag-grid-react";

const FIELD = {
    NAME:'name',
    AVERAGE:'average',
    SKILLS_SELECTED:'skillsSelected',
    SKILL_AMOUNT:'skillAmount',
    SKILL_RESULTS:'skillResults'
}
const SELECT_TYPE = {
    MANUAL: {val:0, name:'Manual'},
    WIZARD: {val:1, name:'Wizard'}
}
const ACTION = {
    UPDATE_FILTERS: 'updateFilters',
    UPDATE_TEXTBOOK_AND_CURRICULUM_DATA: 'updateTextbookAndCurriculumData',
    UPDATE_TABLE_DATA: 'updatetableData',
    UPDATE_STUDENTS: 'updateStudents',
    UPDATE_STUDENTS_SKILL_IDS: 'updateStudentsSkillIds',
    UPDATE_SKILLS: 'updateSkills',
}
const FILTER_TYPES = {
    STUDENT_LIST: 'studentList',
    DIFFICULTY_LEVELS: 'difficultyLevels',
    AGGREGATE_VIEW: 'aggregateView',
    GROUP_BY: 'groupBy',
}
const TABLE_DATA_TYPES = {
    RECOMMEND_WORK: 'recommendWork',
    SELECTION_TYPE: 'selectionType',
    TOTAL_COMPLETE: 'totalComplete',
    WIZARD_SKILLS_COUNT: 'wizardSkillsCount',
}

function reducer(state, action) {
    if (action.type === ACTION.UPDATE_FILTERS) {
        return Object.assign({}, state, {
            filters: Object.assign({}, state.filters, {
                [action.payload.filterType]: action.payload.filterValue,
            }),
        });
    } else if (action.type === ACTION.UPDATE_TEXTBOOK_AND_CURRICULUM_DATA) {
        return Object.assign({}, state, {
            textbookData: action.payload.textbookData,
            curriculumData: action.payload.curriculumData
        });
    } else if (action.type === ACTION.UPDATE_TABLE_DATA) {
        return Object.assign({}, state, {
            tableData: Object.assign({}, state.tableData, {
                [action.payload.tableDataType]: action.payload.tableDataValue,
            }),
        });
    } else if (action.type === ACTION.UPDATE_STUDENTS) {
        return Object.assign({}, state, {
            students: action.payload.students
        });
    } else if (action.type === ACTION.UPDATE_STUDENTS_SKILL_IDS) {
        let newStudents = {};
        Object.entries(state.students).map(([key, student]) => {
            if (action.payload.userIds.includes(student.userId) && !student.feedbackAssigned) {
                let newSkillIds = student.assignedSkills[state.filters[FILTER_TYPES.AGGREGATE_VIEW]];
                if (action.payload.override) {
                    newSkillIds = action.payload.skillIds;
                } else {
                    const containCheck = action.payload.skillIds.every(value => newSkillIds.includes(value));
                    action.payload.skillIds.map(skillId => {
                        if (!newSkillIds.includes(skillId)) {
                            newSkillIds.push(skillId);
                        } else if (containCheck && !action.payload.checked) {
                            const skillIndex = newSkillIds.indexOf(skillId);
                            if (skillIndex !== -1) newSkillIds.splice(skillIndex, 1);
                        }
                    });
                }
                // action.payload.override
                newStudents[key] = Object.assign({}, student, {
                    assignedSkills: Object.assign({}, student.assignedSkills, {
                        [state.filters[FILTER_TYPES.AGGREGATE_VIEW]]: newSkillIds
                    })
                });
            } else {
                newStudents[key] = student;
            }
        });
        return Object.assign({}, state, {
            students: newStudents
        });
    } else if (action.type === ACTION.UPDATE_SKILLS) {
        return Object.assign({}, state, {
            skills: action.payload.skills
        });
    } else {
        return state;
    }
}

const SkillSelect = props => {
    const {
        stepChange,
        testAssignmentData,
        trackedState,
        updateStudents,
        updateSkillResources,
        updateStudentScores,
        updateTrackedState
    } = props;

    const [isConceptLoading, setConceptLoading] = useState(false);
    const [isSkillResultsLoading, setSkillResultsLoading] = useState(false);
    const [columnDefs, setColumnDefs] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [studentResultsFilterForm] = Form.useForm();
    const brandConfig = useContext(BrandContext);
    const defaultState = {
        filters: {
            [FILTER_TYPES.STUDENT_LIST]: [],
            [FILTER_TYPES.DIFFICULTY_LEVELS]: brandConfig.diagnosticTestLevels,
            [FILTER_TYPES.AGGREGATE_VIEW]: COMPARISON_TYPE.TEXT.val,
            [FILTER_TYPES.GROUP_BY]: {
                [COMPARISON_TYPE.TEXT.val]: AGGREGATE_LEVELS.SKILL,
                [COMPARISON_TYPE.CURRICULUM.val]: AGGREGATE_LEVELS.SKILL,
            },
        },
        textbookData: [],
        curriculumData: [],
        tableData: {
            [TABLE_DATA_TYPES.RECOMMEND_WORK]: true,
            [TABLE_DATA_TYPES.SELECTION_TYPE]: SELECT_TYPE.WIZARD.val,
            [TABLE_DATA_TYPES.TOTAL_COMPLETE]: 0,
            [TABLE_DATA_TYPES.WIZARD_SKILLS_COUNT]: 1,
        },
        students: {},
        skills: {},
    }
    const [state, dispatch] = useReducer(reducer, Object.entries(trackedState).length > 0 ? trackedState : defaultState);

    const TOOLTIP = {
        RECOMMEND: 'You can assign ' + brandConfig.diagnosticFeedbackName.toLowerCase() + ' for students based on their performance in the below mathematical skills. ' +
            'Students will be notified when you assign ' + brandConfig.diagnosticFeedbackName.toLowerCase() + '.',
        WIZARD_WHAT: 'The Wizard recommends ' + brandConfig.diagnosticFeedbackName.toLowerCase() + ' by prioritising mathematical skills that a student has' +
            ' struggled with the most compared to the rest of the class, up to a quota set by you.',
        WIZARD_HOW_ONE: 'It first selects mathematical skills based on the discrepancy between the student’s ' +
            'performance against a skill compared to the class average, starting from the largest difference.',
        WIZARD_HOW_TWO: 'If the quota for the number of skills has not been reached using this method' +
            ' (because a student has performed above the class average for some or all of the skills), ' +
            'the Wizard then selects skills based on the student’s performance against the skill ' +
            'independently of the class average, starting from their lowest performance.'
    }

    const handleStudentChange = (studentIds) => {
        studentResultsFilterForm.setFieldsValue({['studentIds']:studentIds});
        studentIds.sort((a, b) => {
            const indexOfA = Object.keys(state.students).indexOf(a.toString());
            const indexOfB = Object.keys(state.students).indexOf(b.toString());
            return indexOfA - indexOfB;
        });
        dispatch({type: ACTION.UPDATE_FILTERS, payload: {filterType: FILTER_TYPES.STUDENT_LIST, filterValue: studentIds}});
    }

    const handleAllStudentChange = (event) => {
        let newStudentsList = [];
        if (event.target.checked) {
            newStudentsList = Object.values(state.students).map(student => student.userId);
        }
        handleStudentChange(newStudentsList);
    }

    const handleDifficultyChange = (levels) => {
        dispatch({type: ACTION.UPDATE_FILTERS, payload: {filterType: FILTER_TYPES.DIFFICULTY_LEVELS, filterValue: levels.sort()}});
    }

    const handleContentTypeChange = ({target}) => {
        dispatch({type: ACTION.UPDATE_FILTERS, payload: {filterType: FILTER_TYPES.AGGREGATE_VIEW, filterValue: target.value}});
    }

    const handleGroupByChange = (value, comparisonType) => {
        const newGroupBy = Object.assign({}, state.filters[FILTER_TYPES.GROUP_BY], {
            [comparisonType]: value
        });
        dispatch({type: ACTION.UPDATE_FILTERS, payload: {filterType: FILTER_TYPES.GROUP_BY, filterValue: newGroupBy}});
    }

    const handleRecommendWorkChange = (value) => {
        dispatch({type: ACTION.UPDATE_TABLE_DATA, payload: {tableDataType: TABLE_DATA_TYPES.RECOMMEND_WORK, tableDataValue: value}});
    }

    const handleSelectionTypeChange = ({target:{ value }}) => {
        const newSelectionType = Object.values(SELECT_TYPE).find(type => type.val === value);
        dispatch({type: ACTION.UPDATE_TABLE_DATA, payload: {tableDataType: TABLE_DATA_TYPES.SELECTION_TYPE, tableDataValue: newSelectionType.val}});
    }

    const handleSkillSelectChange = (userIds, skillIds, checked) => {
        dispatch({type: ACTION.UPDATE_STUDENTS_SKILL_IDS, payload: {userIds: userIds, skillIds: skillIds, checked: checked}});
    }

    const handleSkillCountForWizardChange = (count) => {
        dispatch({type: ACTION.UPDATE_TABLE_DATA, payload: {tableDataType: TABLE_DATA_TYPES.WIZARD_SKILLS_COUNT, tableDataValue: count}});
    }

    const handleAutoSelectSkills = (event)=>{
        autoSelectSkillsForStudents();
    }

    const backToAssignFollowUpWork = ()=> {
        stepChange(STEP_NUMBERS.TEST_ASSIGNMENTS.number);
    }

    const handleConfirmSelection = async () => {
        const studentSkillIdsMap = {};
        Object.entries(state.students).map(([key, student]) => {
            if (student.assignedSkills[state.filters[FILTER_TYPES.AGGREGATE_VIEW]].length > 0) {
                studentSkillIdsMap[student.userId] = student.assignedSkills[state.filters[FILTER_TYPES.AGGREGATE_VIEW]];
            }
        });
        if (Object.keys(studentSkillIdsMap).length === 0) {
            displayToastMessage('Error, no skills selected for students.', '', 'warning', null, 10000);
            return;
        }
        await axios.post('/web/ui/test/getStudentsSkillResourceIds', studentSkillIdsMap)
            .then((response) => {
                if (Object.keys(response.data).length === 0) {
                    displayToastMessage('Error finding skill resources', response.data.message, 'error', null, 10000);
                } else {
                    updateSkillResources(response.data);
                    stepChange(STEP_NUMBERS.SKILL_RESOURCE_SELECT.number);
                }
            }).catch((error) => {
                console.error('Error generating skill resources for skills: ', error);
            });
    }

    const calculateMarkPercentageFromSkill = (skill) => {
        if (state.filters[FILTER_TYPES.DIFFICULTY_LEVELS].length < brandConfig.diagnosticTestLevels.length && state.filters[FILTER_TYPES.DIFFICULTY_LEVELS].length > 0) {
            let skillMark = 0,
                skillMaxMark = 0;
            state.filters[FILTER_TYPES.DIFFICULTY_LEVELS].forEach((level) => {
                skillMark += skill['markLevel' + level];
                skillMaxMark += skill['maxMarkLevel' + level];
            });

            if (skillMaxMark === 0) {
                return GRID_ENUMS.NOT_AVAILABLE;
            } else if (skillMark === 0) {
                return 0;
            } else {
                return Math.round(skillMark * 100 / skillMaxMark);
            }
        } else {
            return skill.markPercent;
        }
    }

    const createSkillMarkObject = (skill) => {
        let skillMarkObject = {markPercent: skill.markPercent, marks: {}, maxMarks: {}};

        state.filters[FILTER_TYPES.DIFFICULTY_LEVELS].forEach((level) => {
            skillMarkObject.marks['markLevel' + level] = skill['markLevel' + level];
            skillMarkObject.maxMarks['maxMarkLevel' + level] = skill['maxMarkLevel' + level];
        });

        return skillMarkObject
    }

    const calculateSkillMarkPercentage = (skillMarkObject) => {
        if (state.filters[FILTER_TYPES.DIFFICULTY_LEVELS].length < brandConfig.diagnosticTestLevels.length && state.filters[FILTER_TYPES.DIFFICULTY_LEVELS].length > 0) {
            let skillMark = 0,
                skillMaxMark = 0;
            state.filters[FILTER_TYPES.DIFFICULTY_LEVELS].forEach((level) => {
                skillMark += skillMarkObject.marks['markLevel' + level];
                skillMaxMark += skillMarkObject.maxMarks['maxMarkLevel' + level];
            });

            if (skillMaxMark === 0) {
                return GRID_ENUMS.NOT_AVAILABLE;
            } else if (skillMark === 0) {
                return 0;
            } else {
                return Math.round(skillMark * 100 / skillMaxMark);
            }
        } else {
            return skillMarkObject.markPercent;
        }
    }

    const getTextbookAndCurriculumConceptsAndSkills = () => {
        setConceptLoading(true);
        axios.get('/web/ui/test/getTestConceptSkillSummary',
            {params: {testId: testAssignmentData.testId}})
            .then((response) => {
                loadTextbookAndCurriculumData(response.data.first, response.data.second);
            }).catch((error) => {
            setConceptLoading(false);
                console.error('Error getting skills and concepts for test', error);
            });
    }

    const loadSkillResults = ()=> {
        setSkillResultsLoading(true);
        axios.get('/web/ui/test/getTestAssignmentTestResultsWithSkillMarks',
            {params: {testAssignmentId: testAssignmentData.testAssignmentId, populateSkillResources: false}})
            .then((response)=>{
                loadStudents(response.data);
            }).catch((error)=>{
            setSkillResultsLoading(false);
                console.error('Error test results with skills: ',error);
            });
    }

    const loadTextbookAndCurriculumData = (textbookData, curriculumData) => {
        const buildData = (dataObj, groupBys) => {
            let newDataObj = {};
            dataObj.map((conceptData) => {
                const skillIds = conceptData.skills.map(skill => skill.skillId);
                if (!newDataObj[conceptData.topicId]) newDataObj[conceptData.topicId] = buildLevelTemplate(conceptData.topicId, conceptData.topicName, groupBys[0]);
                newDataObj[conceptData.topicId].skillIds.push(...skillIds);
                newDataObj[conceptData.topicId].children[conceptData.conceptId] = buildLevelTemplate(conceptData.conceptId, conceptData.name, groupBys[1]);
                newDataObj[conceptData.topicId].children[conceptData.conceptId].skillIds.push(...skillIds);
                let skills = {};
                conceptData.skills.map(skillData => {
                    skills[skillData.skillId] = buildLevelTemplate(skillData.skillId, skillData.name, groupBys[2], false);
                    skills[skillData.skillId].skillIds = [skillData.skillId];
                });
                newDataObj[conceptData.topicId].children[conceptData.conceptId].children = skills;
            });
            return newDataObj;
        }
        const buildLevelTemplate = (id, name, groupBy, children = true) => {
            return {id: id, skillIds: [], name: name, groupBy: groupBy, children: {}}
        }
        const newTextbookData= buildData(textbookData, [AGGREGATE_LEVELS.TOPIC, AGGREGATE_LEVELS.CONCEPT, AGGREGATE_LEVELS.SKILL]);
        const newCurriculumData = buildData(curriculumData, [AGGREGATE_LEVELS.STRAND, AGGREGATE_LEVELS.SUBSTRAND, AGGREGATE_LEVELS.SKILL]);
        dispatch({type: ACTION.UPDATE_TEXTBOOK_AND_CURRICULUM_DATA, payload: {textbookData: newTextbookData, curriculumData: newCurriculumData}});
        let newSkills = {};
        textbookData.map(conceptData => {
            conceptData.skills.map(skillData => {
                newSkills[skillData.skillId] = {
                    skillId: skillData.skillId,
                    textbookTopicId: String(skillData.textbookTopicId),
                    textbookConceptId: String(skillData.textbookConceptId),
                    curriculumTopicId: String(skillData.curriculumTopicId),
                    curriculumConceptId: String(skillData.curriculumConceptId),
                }
            });
        });
        dispatch({type: ACTION.UPDATE_SKILLS, payload: {skills: newSkills}})
        setConceptLoading(false);
    }

    const loadStudents = studentsData => {
        let newStudents = {};
        let newStudentList = [];
        let newTotalComplete = 0;
        let studentsForParent = [];
        let studentsScoresForParent = {}
        studentsData.forEach(studentData => {
            let assignedSkills = studentData.assignedSkillIds ? [...studentData.assignedSkillIds] : [];
            let newStudentData = {
                userId: studentData.userId,
                name: studentData.lastName + ', ' + studentData.firstName,
                assignedSkills: {
                    [COMPARISON_TYPE.TEXT.val]: assignedSkills,
                    [COMPARISON_TYPE.CURRICULUM.val]: assignedSkills,
                },
                showResult: studentData.showResult,
                feedbackAssigned: studentData.feedbackAssigned,
            };
            let scoreTotal = 0;
            let answeredSkillCount = 0;
            let newTestResultSkills = {};
            let studentScoresForParent = {};
            studentData.testResultSkills.map(skillData => {
                const markObject = createSkillMarkObject(skillData);
                const markPercent = calculateSkillMarkPercentage(markObject);
                if (String(markPercent) !== GRID_ENUMS.NOT_AVAILABLE) {
                    answeredSkillCount++;
                    scoreTotal += markPercent;
                    newTestResultSkills[skillData.skillId] = {
                        skillId: skillData.skillId,
                        score: markPercent,
                        markObject: markObject,
                        disabled: markPercent === GRID_ENUMS.NOT_AVAILABLE || studentData.feedbackAssigned
                    }
                }
                studentScoresForParent[skillData.skillId] = markPercent;
            });
            if (studentData.testResultSkills.length > 0) {
                newStudentData.average = (scoreTotal === 0 || answeredSkillCount === 0) ? 0 : Math.round(scoreTotal / answeredSkillCount);
            } else {
                newStudentData.average = 0;
            }
            newStudentData.testAttempted = studentData.testResultSkills.length > 0;
            newStudentData.testResultSkills = newTestResultSkills;
            studentsForParent.push({userId: studentData.userId, name: studentData.lastName + ', ' + studentData.firstName});
            studentScoresForParent.testScore = studentData.startDate ? newStudentData.average + '%' : GRID_ENUMS.NOT_AVAILABLE;
            studentsScoresForParent[studentData.userId] = studentScoresForParent;
            let belowAverage = [];
            let otherScores = [];
            Object.entries(newTestResultSkills).map(([key, testResultSkill]) => {
                if (testResultSkill.score < newStudentData.average) {
                    belowAverage.push({skillId: testResultSkill.skillId, score: testResultSkill.score});
                } else {
                    otherScores.push({skillId: testResultSkill.skillId, score: testResultSkill.score});
                }
            });
            newStudentData.belowAverage = belowAverage.sort((a, b) => (a.score < b.score ? -1 : 0));
            newStudentData.otherScores = otherScores.sort((a, b) => (a.score < b.score ? -1 : 0));
            newStudents[studentData.userId] = newStudentData;
            newStudentList.push(studentData.userId);
            if (studentData.completeDate !== undefined) {
                newTotalComplete++;
            }
        });
        dispatch({type: ACTION.UPDATE_STUDENTS, payload: {students: newStudents}});
        handleStudentChange(newStudentList);
        dispatch({type: ACTION.UPDATE_TABLE_DATA, payload: {tableDataType: TABLE_DATA_TYPES.TOTAL_COMPLETE, tableDataValue: newTotalComplete}});
        updateStudents(studentsForParent);
        updateStudentScores(studentsScoresForParent);
        setSkillResultsLoading(false);
    }

    const buildColumnDefs = (state) => {
        const buildColumnFromData = (data, groupBy) => {
            let columnDefs = [];
            Object.entries(data).map(([key, obj]) => {
                let newColumnDef = {
                    headerName: obj.name,
                    field: obj.id.toString(),
                    groupId: obj.id.toString() + '-' + obj.groupBy,
                    colId: obj.id.toString() + '-' + obj.groupBy
                }
                if (obj.groupBy !== groupBy && obj.children && Object.entries(obj.children).length > 0) newColumnDef.children = buildColumnFromData(obj.children, groupBy);
                columnDefs.push(newColumnDef);
            });
            return columnDefs;
        }
        let newColumnDefs = [];
        newColumnDefs.push({field: FIELD.NAME, cellStyle: null, pinned: 'left'});
        const data = state.filters[FILTER_TYPES.AGGREGATE_VIEW] === COMPARISON_TYPE.TEXT.val ? state.textbookData : state.curriculumData;
        newColumnDefs.push(...buildColumnFromData(data, state.filters[FILTER_TYPES.GROUP_BY][state.filters[FILTER_TYPES.AGGREGATE_VIEW]]));
        newColumnDefs.push({field: FIELD.AVERAGE, pinned: 'right'});
        return newColumnDefs;
    }

    const autoSelectSkillsForStudents = ()=> {
        if (state.tableData[TABLE_DATA_TYPES.WIZARD_SKILLS_COUNT] > Object.values(state.skills).length) {
            displayToastMessage('Error, wizard input number is higher than available skills.', '', 'warning',null, 10000);
            return;
        }
        Object.entries(state.students).map(([key, student]) => {
            if (student.testAttempted && !student.feedbackAssigned) {
                let belowAverageThenPerformance = [];
                let newAssignedSkills = [];
                Object.entries(student.belowAverage).map(([key, obj]) => {
                    if (Object.values(state.skills).some(skill => skill.skillId === obj.skillId)) belowAverageThenPerformance.push(obj.skillId);
                });
                Object.entries(student.otherScores).map(([key, obj]) => {
                    if (Object.values(state.skills).some(skill => skill.skillId === obj.skillId)) belowAverageThenPerformance.push(obj.skillId);
                });
                for (let i = 0; i < state.tableData[TABLE_DATA_TYPES.WIZARD_SKILLS_COUNT]; i++) {
                    newAssignedSkills.push(belowAverageThenPerformance[i]);
                }
                dispatch({type: ACTION.UPDATE_STUDENTS_SKILL_IDS, payload: {userIds: [student.userId], skillIds: newAssignedSkills, override: true}});
            }
        });
    }

    const buildRowData = (state) => {
        let newRowData = [];
        const formatPercentage = (percentage) => {
            if (isNaN(percentage)) return undefined;
            return percentage + (percentage !== GRID_ENUMS.NOT_AVAILABLE ? '%' : '')
        }
        state.filters[FILTER_TYPES.STUDENT_LIST].map(studentId => {
            const student = state.students[studentId];
            let skillGrouping = {}
            Object.entries(student.testResultSkills).map(([key, testResultSkill]) => {
                const skill = state.skills[testResultSkill.skillId];
                if(skill != undefined) {
                    if (state.filters[FILTER_TYPES.AGGREGATE_VIEW] === COMPARISON_TYPE.TEXT.val) {
                        if (state.filters[FILTER_TYPES.GROUP_BY][COMPARISON_TYPE.TEXT.val] === AGGREGATE_LEVELS.TOPIC) {
                            if (!skillGrouping[skill.textbookTopicId]) skillGrouping[skill.textbookTopicId] = [];
                            skillGrouping[skill.textbookTopicId].push(testResultSkill);
                        } else if (state.filters[FILTER_TYPES.GROUP_BY][COMPARISON_TYPE.TEXT.val] === AGGREGATE_LEVELS.CONCEPT) {
                            if (skillGrouping[skill.textbookConceptId] === undefined) skillGrouping[skill.textbookConceptId] = [];
                            skillGrouping[skill.textbookConceptId].push(testResultSkill);
                        } else {
                            if (!skillGrouping[skill.skillId]) skillGrouping[skill.skillId] = [];
                            skillGrouping[skill.skillId].push(testResultSkill);
                        }
                    } else {
                        if (state.filters[FILTER_TYPES.GROUP_BY][COMPARISON_TYPE.CURRICULUM.val] === AGGREGATE_LEVELS.STRAND) {
                            if (!skillGrouping[skill.curriculumTopicId]) skillGrouping[skill.curriculumTopicId] = [];
                            skillGrouping[skill.curriculumTopicId].push(testResultSkill);
                        } else if (state.filters[FILTER_TYPES.GROUP_BY][COMPARISON_TYPE.CURRICULUM.val] === AGGREGATE_LEVELS.SUBSTRAND) {
                            if (skillGrouping[skill.curriculumConceptId] === undefined) skillGrouping[skill.curriculumConceptId] = [];
                            skillGrouping[skill.curriculumConceptId].push(testResultSkill);
                        } else {
                            if (!skillGrouping[skill.skillId]) skillGrouping[skill.skillId] = [];
                            skillGrouping[skill.skillId].push(testResultSkill);
                        }
                    }
                }
            });
            let newStudentRow = {};
            let totalPercentage = 0;
            let skillTotalAnsweredCount = 0;
            newStudentRow[FIELD.NAME] = {value: student.name, userId: student.userId, disabled: !student.testAttempted || student.feedbackAssigned};
            Object.entries(skillGrouping).forEach(([key, skillGroup]) => {
                let groupedPercentage = 0;
                let skillIds = [];
                let skillAnsweredCount = 0;
                skillGroup.map(testResultSkill => {
                    let markPercent = calculateSkillMarkPercentage(testResultSkill.markObject);
                    if (String(markPercent) !== GRID_ENUMS.NOT_AVAILABLE) {
                        skillAnsweredCount++;
                        groupedPercentage += markPercent;
                        skillIds.push(testResultSkill.skillId);
                    }
                });

                if (skillAnsweredCount > 0) {
                    skillTotalAnsweredCount++;
                }

                const averagedGroupedPercentage = Math.round(groupedPercentage / skillAnsweredCount);
                newStudentRow[key] = {value: formatPercentage(averagedGroupedPercentage), skillIds: skillIds, disabled: skillAnsweredCount === 0};
                totalPercentage += !isNaN(averagedGroupedPercentage) ? averagedGroupedPercentage : 0;
            });
            const averagedTotalPercentage = Math.round(totalPercentage / skillTotalAnsweredCount);
            newStudentRow[FIELD.AVERAGE] = {value: student.testAttempted ? formatPercentage(averagedTotalPercentage) : 'Test not attempted.'};
            newRowData.push(newStudentRow);
        });
        return newRowData;
    }

    function tableHeaderRenderer(params) {
        const headerValue = params.displayName;
        if (headerValue && headerValue !== '') {
            if (state.tableData[TABLE_DATA_TYPES.RECOMMEND_WORK] && headerValue.toLowerCase() !== FIELD.NAME && headerValue.toLowerCase() !== FIELD.AVERAGE) {
                // Get checkboxes id and level.
                let idKey;
                if (params.columnGroup) idKey = params.columnGroup.groupId;
                else if (params.column) idKey = params.column.colId;
                const [id, level] = idKey.split('-');
                // Find skills for the id.
                const findSkillIdsFromData = (data) => {
                    let skillIds = [];
                    for (const [key, obj] of Object.entries(data)) {
                        if (obj.id.toString() === id && obj.groupBy === level) {
                            skillIds = obj.skillIds;
                        } else if (obj.children && Object.entries(obj.children).length > 0) {
                            let childrenSkills = findSkillIdsFromData(obj.children);
                            if (childrenSkills.length > 0) skillIds = childrenSkills;
                        }
                        if (skillIds.length > 0) break;
                    }
                    return skillIds;
                }
                const data = state.filters[FILTER_TYPES.AGGREGATE_VIEW] === COMPARISON_TYPE.TEXT.val ? state.textbookData : state.curriculumData;
                const headerSkillIds = findSkillIdsFromData(data);
                // Check if the students have the ids skills selected.
                let indeterminate = false;
                let checked = true;
                for (const studentId of state.filters[FILTER_TYPES.STUDENT_LIST]) {
                    const student = state.students[studentId];
                    if (!student.feedbackAssigned) {
                        const userSkillIds = student.assignedSkills[state.filters[FILTER_TYPES.AGGREGATE_VIEW]];
                        if (userSkillIds.length > 0) {
                            indeterminate = headerSkillIds.some(value => userSkillIds.includes(value));
                            checked = headerSkillIds.every(value => userSkillIds.includes(value));
                        } else {
                            checked = false;
                        }
                        if (!checked) break;
                    }
                }
                if (checked) indeterminate = false;

                return (
                    <div className={"wrap-table-header"}>
                        <Checkbox
                            label={headerValue}
                            indeterminate={indeterminate}
                            checked={checked}
                            onClick={(event) => handleSkillSelectChange(state.filters[FILTER_TYPES.STUDENT_LIST], headerSkillIds, event.target.checked)}
                        />
                    </div>
                )
            } else {
                return (
                    <div className={"wrap-table-header"}>
                        <div className={'label'}>{headerValue}</div>
                    </div>
                )
            }
        } else {
            return '';
        }
    }

    function tableCellRenderer(params) {
        const fieldName = params.colDef.field;
        const userId = params.data[FIELD.NAME].userId;
        const cellValue = params.value && params.value.value ? params.value.value : GRID_ENUMS.NOT_AVAILABLE;
        const cellSkillIds = params.value && params.value.skillIds ? params.value.skillIds : (fieldName === FIELD.NAME ? Object.entries(state.students[userId].testResultSkills).map(([key, skill]) => skill.skillId) : []);
        const cellValueInt = !isNaN(parseInt(cellValue)) ? parseInt(cellValue) : -1;
        const showResult = state.students[userId].showResult;
        if (state.tableData[TABLE_DATA_TYPES.RECOMMEND_WORK] && state.students[userId].testAttempted && fieldName !== FIELD.AVERAGE) {
            if (fieldName === FIELD.NAME && params.value.disabled) {
                return (
                    <div>
                        <span>{fieldName === FIELD.NAME && <Checkbox label={cellValue} disabled/>}</span>
                    </div>
                )
            } else {
                let checked = false;
                for (const studentId of state.filters[FILTER_TYPES.STUDENT_LIST]) {
                    const student = state.students[studentId];
                    if (student.userId === userId) {
                        checked = cellSkillIds.every(value => student.assignedSkills[state.filters[FILTER_TYPES.AGGREGATE_VIEW]].includes(value));
                    }
                }

                return (
                    <Checkbox
                        label={cellValue}
                        disabled={!params.value || params.data.name.disabled || params.value.disabled}
                        checked={checked}
                        onClick={(event) => handleSkillSelectChange([userId], cellSkillIds, event.target.checked)}
                    />
                )
            }
        } else {
            return (
                <div>{cellValue}</div>
            )
        }
    }

    useEffect(() => {
        if (Object.entries(trackedState).length === 0) {
            getTextbookAndCurriculumConceptsAndSkills();
            loadSkillResults();
        }
    }, []);

    useEffect(() => {
        setColumnDefs(buildColumnDefs(state));
        if (Object.keys(state.skills).length > 0) {
            setRowData(buildRowData(state));
        }
        updateTrackedState(state);
    }, [state.skills, state.filters, state.tableData, state.students]);

    return (
        <div>
            <Space className="flex flexSpaceBetween padding-y--m" orientation="horizontal" size="middle" style={{width: '100%'}}>
                <h2>Student results ({testAssignmentData.testName})</h2>
                <div className="flex">
                    <ButtonsGroup>
                        <Button type="outline" onClick={backToAssignFollowUpWork}><ArrowLeftIcon/> Back to assigned tests</Button>
                        <PopConfirm
                            placement="left"
                            title="Confirm your skill selection?"
                            description="Confirm selection"
                            onConfirm={handleConfirmSelection}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button disabled={state.tableData[TABLE_DATA_TYPES.TOTAL_COMPLETE] === 0}>Confirm</Button>
                        </PopConfirm>
                    </ButtonsGroup>
                </div>
            </Space>
            <Space orientation="vertical" className="flex flexSpaceBetween" size="middle">
                <Card id="studentResultSearchBox" rounded={false}>
                    <h4>Filter</h4>
                    <Form
                        id="studentResultsFilterForm"
                        className="flex margin-t--m"
                        form={studentResultsFilterForm}
                        initialValues={{
                            studentIds: state.filters[FILTER_TYPES.STUDENT_LIST],
                            difficultyLevels: state.filters[FILTER_TYPES.DIFFICULTY_LEVELS],
                            contentType: state.filters[FILTER_TYPES.AGGREGATE_VIEW],
                            groupByTextContentLevel: state.filters[FILTER_TYPES.GROUP_BY][COMPARISON_TYPE.TEXT.val],
                            groupByCurriculumContentLevel: state.filters[FILTER_TYPES.GROUP_BY][COMPARISON_TYPE.CURRICULUM.val],
                        }}
                        layout="vertical"
                        style={{gap: '8px'}}
                    >
                        <Form.Item
                            label="Student"
                            name="studentIds"
                            style={{width: '50%'}}
                        >
                            <Select
                                size="small"
                                style={{minWidth: '200px'}}
                                virtual={false}
                                showSearch={false}
                                popupRender={menu => (
                                    <div>
                                        {menu}
                                        <Divider className='margin-b--rm'/>
                                        <Checkbox
                                            label={'Select all students'}
                                            className={'padding--m'}
                                            disabled={Object.keys(state.students).length === 0}
                                            value={state.filters[FILTER_TYPES.STUDENT_LIST]}
                                            defaultChecked={true}
                                            checked={state.filters[FILTER_TYPES.STUDENT_LIST].length === Object.keys(state.students).length}
                                            onChange={handleAllStudentChange}
                                        />
                                    </div>
                                )}
                                showarrow={'true'}
                                allowClear={true}
                                autoClearSearchValue={true}
                                onChange={handleStudentChange}
                                mode="multiple"
                                maxTagCount="responsive"
                                getPopupContainer={trigger => trigger.parentElement}
                            >
                                {Object.values(state.students).sort((a, b) => a.name.localeCompare(b.name)).map((student) => (
                                    <Select.Option key={student.userId} value={student.userId}>
                                        {student.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="Difficulty"
                            name="difficultyLevels"
                            style={{maxWidth: '200px'}}
                        >
                            <Select showSearch={false} showarrow={'true'} allowClear mode="multiple"
                                onChange={handleDifficultyChange}
                                style={{minWidth: '80px'}}
                                size="small"
                                getPopupContainer={trigger => trigger.parentElement}
                            >
                                {brandConfig.diagnosticTestLevels.map((level) => {
                                    return <Select.Option key={level} value={level}>{level}</Select.Option>
                                })}
                            </Select>
                        </Form.Item>
                        <div>
                            <div className="flexRow flexAlignCentre">
                                <div className="flex">Aggregate view</div>
                                <div className="flex">
                                    <Tooltip
                                        title={(
                                            <div><p>This will allow you to see student performance at various textbook/curriculum levels.</p></div>
                                        )}
                                        arrow={{ pointAtCenter: true }}>
                                        <span className="flex margin-l--s">
                                            <HelpIcon height={18} width={18} />
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                            <Form.Item
                                name="contentType"
                            >
                                <Radio.Group
                                    size="small"
                                    onChange={handleContentTypeChange}
                                    // disabled={aggregateDisabled}
                                    style={{minWidth: '132px'}}
                                >
                                    <Radio.Button value={COMPARISON_TYPE.TEXT.val}>{COMPARISON_TYPE.TEXT.properDisplay}</Radio.Button>
                                    <Radio.Button
                                        disabled={Object.entries(state.curriculumData).length === 0}
                                        value={COMPARISON_TYPE.CURRICULUM.val}>{brandConfig.curriculumName}</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </div>
                        { state.filters[FILTER_TYPES.AGGREGATE_VIEW] === COMPARISON_TYPE.TEXT.val && (
                            <Form.Item
                                name="groupByTextContentLevel"
                                label="Group by"
                            >
                                <Select
                                    onChange={(value) => handleGroupByChange(value, COMPARISON_TYPE.TEXT.val)}
                                    // disabled={aggregateDisabled}
                                    getPopupContainer={trigger => trigger.parentElement}
                                    size="small">
                                        <Select.Option key={AGGREGATE_LEVELS.TOPIC} value={AGGREGATE_LEVELS.TOPIC}>Chapter</Select.Option>
                                        <Select.Option key={AGGREGATE_LEVELS.CONCEPT} value={AGGREGATE_LEVELS.CONCEPT}>Section</Select.Option>
                                        <Select.Option key={AGGREGATE_LEVELS.SKILL} value={AGGREGATE_LEVELS.SKILL}>Skill</Select.Option>
                                </Select>
                            </Form.Item>
                        )}
                        { state.filters[FILTER_TYPES.AGGREGATE_VIEW] === COMPARISON_TYPE.CURRICULUM.val && (
                            <Form.Item
                                name="groupByCurriculumContentLevel"
                                label="Group by"
                            >
                                <Select
                                    onChange={(value) => handleGroupByChange(value, COMPARISON_TYPE.CURRICULUM.val)}
                                    // disabled={aggregateDisabled}
                                    getPopupContainer={trigger => trigger.parentElement}
                                    size="small">
                                        <Select.Option key={AGGREGATE_LEVELS.STRAND} value={AGGREGATE_LEVELS.STRAND}>{brandConfig.curriculumTopicName}</Select.Option>
                                        <Select.Option key={AGGREGATE_LEVELS.SUBSTRAND} value={AGGREGATE_LEVELS.SUBSTRAND}>{brandConfig.curriculumConceptName}</Select.Option>
                                        <Select.Option key={AGGREGATE_LEVELS.SKILL} value={AGGREGATE_LEVELS.SKILL}>{brandConfig.curriculumSkillName}</Select.Option>
                                </Select>
                            </Form.Item>
                        )}
                    </Form>
                </Card>
                <Card rounded={false}>
                    <div className="flex flexSpaceBetween">
                        <div>
                            <div className="flexRow flexAlignCentre">
                                <h4>Recommend {brandConfig.diagnosticFeedbackName.toLowerCase()} for students</h4>
                                <MuiTooltip placement={"top"} title={<div className={"tooltip-wrapper"}>{TOOLTIP.RECOMMEND}</div>}>
                                    <span className="flex margin-l--s">
                                        <HelpIcon height={18} width={18} />
                                    </span>
                                </MuiTooltip>
                                <span className="ch-center-elem">
                                    <Switch
                                        className="margin-l--m ch-center-elem"
                                        size="small"
                                        defaultChecked={state.tableData[TABLE_DATA_TYPES.RECOMMEND_WORK]}
                                        disabled={state.tableData[TABLE_DATA_TYPES.TOTAL_COMPLETE] === 0}
                                        onChange={handleRecommendWorkChange}
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex margin-t--m">
                        <div className="ch-center-elem">
                            <label>Selection type:</label>
                            <Radio.Group
                                className="margin-l--s flex flexNoWrap"
                                size="small"
                                defaultValue={state.tableData[TABLE_DATA_TYPES.SELECTION_TYPE]}
                                onChange={handleSelectionTypeChange}
                                disabled={state.tableData[TABLE_DATA_TYPES.TOTAL_COMPLETE] === 0}
                            >
                                <Radio.Button value={SELECT_TYPE.MANUAL.val}>{SELECT_TYPE.MANUAL.name}</Radio.Button>
                                <Radio.Button value={SELECT_TYPE.WIZARD.val}>{SELECT_TYPE.WIZARD.name}</Radio.Button>
                            </Radio.Group>
                            <MuiTooltip placement={"top"} title={<div><div className="ch-lrg-font">Wizard</div><div>{TOOLTIP.WIZARD_WHAT}</div></div>} >
                                <span className="flex margin-l--s">
                                    <HelpIcon height={18} width={18} />
                                </span>
                            </MuiTooltip>
                        </div>
                    </div>
                    <div className="flex margin-y--m">
                        {state.tableData[TABLE_DATA_TYPES.SELECTION_TYPE] === SELECT_TYPE.MANUAL.val && (
                            <div className="ch-sml-font">
                                *Please select skills that you would like to recommend your students to
                                complete {brandConfig.diagnosticFeedbackName.toLowerCase()} for from the table and click confirm to continue.
                            </div>
                        )}
                        {state.tableData[TABLE_DATA_TYPES.SELECTION_TYPE] === SELECT_TYPE.WIZARD.val && (
                            <div className="flexRow flexAlignCentre">
                                <div className="flex">
                                    <span className="ch-sml-font">Limit # of {brandConfig.diagnosticFeedbackName.toLowerCase()} to</span>
                                </div>
                                <div className="flex">
                                    <InputNumber className="margin-x--s" style={{width: '48px'}} size="small" min={1} defaultValue={1} max={Object.entries(state.skills).length} onChange={handleSkillCountForWizardChange}/>
                                </div>
                                <div className="flex">
                                    <span className="ch-sml-font ch-center-elem">skills per student.</span>
                                </div>
                                <div className="flex">
                                    <MuiTooltip placement={"top"} title={<div>
                                        <div className="ch-lrg-font">How the wizard works</div>
                                        <ul>
                                            <li>{TOOLTIP.WIZARD_HOW_ONE}</li>
                                            <li>{TOOLTIP.WIZARD_HOW_TWO}</li>
                                        </ul>
                                    </div>}>
                                        <span className="flex margin-l--s">
                                            <HelpIcon height={18} width={18} />
                                        </span>
                                    </MuiTooltip>
                                </div>
                                <Button className="margin-l--m" disabled={!state.tableData[TABLE_DATA_TYPES.RECOMMEND_WORK]} onClick={handleAutoSelectSkills}>Generate</Button>
                            </div>
                        )}
                    </div>
                    <div id="skill-select-view"  className="ag-theme-balham">
                        <div style={{ width: '100%', height: '80vh' }}>
                            <CircularLoader displayInElement={true} pageCentered={true} visible={isConceptLoading || isSkillResultsLoading} />
                            {(!isConceptLoading && !isSkillResultsLoading && state.tableData[TABLE_DATA_TYPES.TOTAL_COMPLETE] === 0) && <div className="ch-med-font warningMessage margin-l--m">No students have answered yet.</div>}
                            {(!isConceptLoading && !isSkillResultsLoading && state.tableData[TABLE_DATA_TYPES.TOTAL_COMPLETE] > 0) &&
                                <AgGridReact
                                    className={"ch-general-table-ag-grid rounded-top"}
                                    defaultColDef={{
                                        width: 150,
                                        cellStyle: cellStylePercentage,
                                        cellRenderer: tableCellRenderer,
                                        headerComponent: tableHeaderRenderer,
                                        resizable: true,
                                        wrapHeaderText: true,
                                        autoHeaderHeight: true,
                                    }}
                                    defaultColGroupDef={{
                                        marryChildren: true,
                                        headerGroupComponent: tableHeaderRenderer,
                                    }}
                                    columnDefs={columnDefs}
                                    rowData={rowData}
                                    // rowClassRules={rowClassRules}
                                    suppressHorizontalScroll={true}
                                    rowHeight={30}
                                    suppressPropertyNamesCheck={true}
                                    headerHeight={GridStyle.ASSIGN_SKILLS_HEADER_HEIGHT.SKILL_HEIGHT}
                                    groupHeaderHeight={GridStyle.ASSIGN_SKILLS_HEADER_HEIGHT.CONCEPT_HEIGHT}
                                />
                            }
                        </div>
                    </div>
                </Card>
            </Space>
        </div>
    )
}

export default SkillSelect;